import { Box, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { HttpStatusCode } from 'axios';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAlertContext } from '../../../../../context/AlertProvider';
import { useApi } from '../../../../../context/ApiProvider';
import { RateInterface, EditRateInterface } from '../../../../../shared/type/rate.type';
import CustomAlert from '../../../../CustomAlert/CustomAlert';
import CustomChakraButton from '../../../../CustomChakraButton/CustomChakraButton';
import IconComponent from '../../../../IconComponent/IconComponent';
import AddEditRate from './AddEditRate/AddEditRate';
import { StickyScrollableTableContainer } from '../../../../DataTable/StickyScrollableTableContainer';
import { DeletedElementInterface } from '../../../../../shared/type/deletedElement.type';

const defaultDeleteElement = {
  id: 0,
  isOpen: false
};

export const defaultRate = {
  id: 0,
  active: true,
  tenantId: 0,
  name: '',
  price: '00,00',
  organisationUnitIds: [],
  invisible: false,
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
  startDate: `${new Date().toLocaleDateString('en-CA')}`,
  endDate: `${new Date().toLocaleDateString('en-CA')}`,
  totalSalesLimit: null,
  salesLimitForPerson: null,
  possibilityToMakeActive: true
};

const Rates = () => {
  const { id: tenant } = useParams();
  const { apiTenantStakeController } = useApi();
  const { setShow, setAlertProperties } = useAlertContext();
  const columnHelper = createColumnHelper<RateInterface>();

  const [isLoading, setIsLoading] = useState(false);
  const [rates, setRates] = useState<RateInterface[]>([]);
  const [deleteElement, setDeleteElement] = useState<DeletedElementInterface>(defaultDeleteElement);
  const [showAlert, setShowAlert] = useState(false);
  const [editRate, setEditRate] = useState<EditRateInterface>(defaultRate);
  const [addEditRatePopUp, setAddEditRatePopUp] = useState<{
    mode: 'add' | 'edit';
    isOpen: boolean;
  }>({ mode: 'add', isOpen: false });

  const fetchRates = async () => {
    setIsLoading(true);
    try {
      const response = await apiTenantStakeController('').get(`/${tenant}`);
      setRates(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
  }, [tenant]);

  const columns = [
    columnHelper.accessor('name', {
      header: 'Nazwa',
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900 w-[200px] text-wrap">{info.getValue()}</p>
      )
    }),
    columnHelper.accessor('price', {
      header: 'Cena',
      cell: (info) => <p className="text-sm text-grayLight-600 w-24">{info.getValue()}</p>
    }),
    columnHelper.accessor('from', {
      header: 'Od',
      cell: (info) => <p className="text-sm text-grayLight-600 w-24">{info.getValue()}</p>
    }),
    columnHelper.accessor('to', {
      header: 'Do',
      cell: (info) => {
        const isOverDated = new Date(info.getValue()) < new Date();

        return (
          <p className={`text-sm ${isOverDated ? 'text-red-700' : 'text-grayLight-600'} w-24`}>
            {info.getValue()}
          </p>
        );
      }
    }),
    columnHelper.accessor('organisationUnits', {
      header: 'Klasy',
      cell: (info) => (
        <div className="text-sm text-grayLight-600 w-48 flex flex-wrap gap-1">
          {info.getValue().map((unit, index) => (
            <span key={index}>
              {unit.name}
              {index < info.getValue().length - 1 ? ',' : ''}
            </span>
          ))}
        </div>
      )
    }),
    columnHelper.accessor('active', {
      header: 'Status',
      cell: (info) => (
        <p className="text-sm text-grayLight-600">{info.getValue() ? 'Aktywny' : 'Nieaktywny'}</p>
      )
    }),
    columnHelper.accessor('totalSalesLimit', {
      header: 'Limity',
      cell: (info) => {
        const { totalSalesLimit, salesLimitForPerson } = info.row.original;
        return (
          <p className="text-sm text-grayLight-600">
            {totalSalesLimit ? `l. stawek/placówka/dzień: ${totalSalesLimit}` : ''}
            <br />
            {salesLimitForPerson ? `l. stawek/osoba/dzień: ${salesLimitForPerson}` : ''}
          </p>
        );
      }
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Edycja',
      cell: ({ row }) => {
        const rate = row.original;
        return (
          <div className="flex gap-4">
            <button
              className="text-sm font-semibold text-grayLight-600"
              onClick={() => {
                if (rate.active) setShowAlert(true);
                else setDeleteElement({ isOpen: true, id: rate.id });
              }}>
              Usuń
            </button>
            <button
              className="text-sm font-semibold text-grayWarm-950"
              onClick={() => {
                setEditRate({
                  id: rate.id,
                  name: rate.name,
                  price: rate.price,
                  startDate: rate.from,
                  endDate: rate.to,
                  active: rate.active,
                  totalSalesLimit: rate.totalSalesLimit,
                  salesLimitForPerson: rate.salesLimitForPerson,
                  possibilityToMakeActive: rate.possibilityToMakeActive,
                  tenantId: Number(tenant),
                  invisible: rate.invisible,
                  monday: rate.monday,
                  tuesday: rate.tuesday,
                  wednesday: rate.wednesday,
                  thursday: rate.thursday,
                  friday: rate.friday,
                  saturday: rate.saturday,
                  sunday: rate.sunday,
                  organisationUnitIds: rate.organisationUnits.map((u) => u.id)
                });
                setAddEditRatePopUp({ isOpen: true, mode: 'edit' });
              }}>
              Edytuj
            </button>
          </div>
        );
      }
    })
  ];

  return (
    <section className="Rates p-spacing-xl w-full">
      <Box mb={6} className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <Text
            whiteSpace="normal"
            overflow="hidden"
            textOverflow="ellipsis"
            className="font-semibold text-base normal-case text-grayLight-900">
            Stawki
          </Text>
          <Text
            whiteSpace="normal"
            overflow="hidden"
            textOverflow="ellipsis"
            className="w-3/4 font-normal text-sm normal-case text-grayLight-700">
            Tutaj możesz tworzyć i edytować stawki jakie będą obowiązywać w oddziale. Stawki to ceny
            w jakich sprzedajesz posiłki. Stawka obowiązuje w konkretnym zakresie dat, będzie
            widoczna tylko dla wybranych klas i może mieć zdefiniowane limity sprzedażowe. Można też
            przypisać do niej kategorie posiłku.
          </Text>
        </div>
        <CustomChakraButton
          size="md"
          hierarchy="primary"
          iconColor="#292524"
          iconPosition="left"
          icon="plus"
          buttonProps={{ onClick: () => setAddEditRatePopUp({ mode: 'add', isOpen: true }) }}>
          Dodaj stawkę
        </CustomChakraButton>
      </Box>

      <StickyScrollableTableContainer
        columns={columns}
        data={rates}
        isLoading={isLoading}
        enableSearch={true}
      />

      <CustomAlert
        header="Nie można usunąć stawki"
        content="Stawka ma status aktywny i jest przypisana do wydawki."
        confirmButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'primary' }}
        confirmButtonText="Zamknij"
        onConfirmAction={() => setShowAlert(false)}
        cancelButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'secondaryGray' }}
        cancelButtonText="Anuluj"
        onCancelAction={() => setShowAlert(false)}
        handleOpen={showAlert}
        icon={
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-warning-100">
            <IconComponent iconName="info" color="#DC6803" />
          </div>
        }
      />

      <CustomAlert
        header="Usunąć stawkę?"
        content="Usunięcie stawki jest nieodwracalne."
        confirmButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'warning' }}
        confirmButtonText="Usuń"
        onConfirmAction={async () => {
          try {
            const response = await apiTenantStakeController('').delete(
              `/${tenant}/${deleteElement.id}`
            );
            if (response.status === HttpStatusCode.NoContent) {
              setAlertProperties({
                timeout: 9000,
                title: 'Sukces',
                description: `Stawka została usunięta`,
                status: 'success'
              });
              setShow(true);
              
            }
          } catch (error) {
            setAlertProperties({
              timeout: 9000,
              title: 'Błąd',
              description: `Nie udało się usunąć stawki`,
              status: 'error'
            });
            setShow(true);
          } finally {
            fetchRates();
          }
        }}
        cancelButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'secondaryGray' }}
        cancelButtonText="Anuluj"
        onCancelAction={() => {
          setDeleteElement(defaultDeleteElement);
          fetchRates();
        }}
        handleOpen={deleteElement.isOpen}
        icon={
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-error-100">
            <IconComponent iconName="trash" />
          </div>
        }
      />

      {addEditRatePopUp.isOpen && (
        <AddEditRate
          isOpen={addEditRatePopUp.isOpen}
          initData={editRate}
          mode={addEditRatePopUp.mode}
          onClose={() => {
            setAddEditRatePopUp({ isOpen: false, mode: 'add' });
            fetchRates();
          }}
        />
      )}
    </section>
  );
};

export default Rates;
